import type { ReactNode } from "react";
import { TaskGroupBy } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import type { BoardCardField } from "@design-system/Board/Board.types";
import {
    isTaskGroupByAvailable,
    isTaskGroupByInScope,
    type TaskGroupingContext,
} from "../taskListCapabilities";
import { ENTITY_LANE_BY, type TaskBoardLaneBy } from "./taskBoardLanes";
import { getTaskColumnName } from "./taskCardEdits";

/** Swimlane groupings in the Display popover's order, named as there. */
export const TASK_BOARD_LANE_OPTIONS: ReadonlyArray<{
    value: TaskBoardLaneBy;
    label: string;
}> = [
    { value: TaskGroupBy.STATUS, label: "Status" },
    { value: TaskGroupBy.PRIORITY, label: "Priority" },
    { value: TaskGroupBy.ASSIGNEE, label: "Assignee" },
    { value: TaskGroupBy.DUE_DATE, label: "Due date" },
    { value: TaskGroupBy.TASK_GROUP, label: "Task group" },
    { value: TaskGroupBy.CLOSE_STAGE, label: "Stage" },
    { value: TaskGroupBy.CLOSE, label: "Service" },
    { value: ENTITY_LANE_BY, label: "Entity" },
    { value: TaskGroupBy.CLIENT, label: "Client" },
];

/** Whether the page offers `laneBy` as swimlanes: Entity always, the rest as the header picker does. */
export const isTaskBoardLaneByAvailable = ({
    laneBy,
    ...context
}: TaskGroupingContext & { laneBy: TaskBoardLaneBy }): boolean =>
    laneBy === ENTITY_LANE_BY ||
    isTaskGroupByAvailable({ ...context, groupBy: laneBy });

/**
 * The swimlanes the board draws for the stored choice: none when they'd
 * repeat the columns or the page doesn't offer them. While the stages and
 * services load only the page's scope counts, so their lanes don't pop in.
 */
export const getTaskBoardLaneBy = ({
    storedLaneBy,
    groupBy,
    isGroupingDataLoading,
    ...context
}: TaskGroupingContext & {
    storedLaneBy: TaskBoardLaneBy | null;
    groupBy: TaskGroupBy;
    /** Whether the stages or services are still loading. */
    isGroupingDataLoading: boolean;
}): TaskBoardLaneBy | null => {
    if (storedLaneBy === null || storedLaneBy === groupBy) {
        return null;
    }
    const isOffered = isGroupingDataLoading
        ? storedLaneBy === ENTITY_LANE_BY ||
          isTaskGroupByInScope({ ...context, groupBy: storedLaneBy })
        : isTaskBoardLaneByAvailable({ ...context, laneBy: storedLaneBy });
    return isOffered ? storedLaneBy : null;
};

const AXIS_FIELDS: Record<TaskBoardLaneBy, TaskColumn | null> = {
    [TaskGroupBy.STATUS]: TaskColumn.STATUS,
    [TaskGroupBy.PRIORITY]: TaskColumn.PRIORITY,
    [TaskGroupBy.ASSIGNEE]: TaskColumn.ASSIGNEE,
    [TaskGroupBy.DUE_DATE]: null,
    [TaskGroupBy.TASK_GROUP]: TaskColumn.GROUP,
    [TaskGroupBy.CLOSE_STAGE]: TaskColumn.CLOSE_STAGE,
    [TaskGroupBy.CLOSE]: TaskColumn.CLOSE,
    [TaskGroupBy.CLIENT]: TaskColumn.CLIENT,
    [ENTITY_LANE_BY]: TaskColumn.ENTITIES,
};

/**
 * The card fields the columns and lanes already show, so cards leave them
 * off. Due date buckets don't show the date, so it stays.
 */
export const getTaskBoardAxisFields = ({
    groupBy,
    laneBy,
}: {
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
}): ReadonlySet<string> =>
    new Set(
        [
            AXIS_FIELDS[groupBy],
            laneBy === null ? null : AXIS_FIELDS[laneBy],
        ].filter((field) => field !== null),
    );

/**
 * `fields` with the status as the first tag. `status` is false where the
 * columns or lanes show it.
 */
export const withStatusField = ({
    fields,
    status,
}: {
    fields: readonly BoardCardField[];
    status: ReactNode;
}): BoardCardField[] => [
    {
        id: TaskColumn.STATUS,
        placement: "inline",
        label: getTaskColumnName(TaskColumn.STATUS),
        content: status,
    },
    ...fields,
];
